import React, { useState, useCallback, useRef } from 'react';
import { Upload, FileText, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useImportMsProject, useImportCsv, type ColumnMapping } from '@/hooks/useImportExport';
import { ColumnMapper } from './ColumnMapper';

interface ImportDialogProps {
  organizationId: string;
  workspaceId: string;
  onSuccess?: (projectId: string) => void;
  children: React.ReactNode;
}

type Step = 'upload' | 'mapping' | 'importing' | 'done' | 'error';

const ACCEPTED_EXTENSIONS = ['.xml', '.csv', '.xlsx'];

function detectFormat(filename: string): 'ms-project' | 'csv' | null {
  const ext = filename.toLowerCase().split('.').pop();
  if (ext === 'xml') return 'ms-project';
  if (ext === 'csv' || ext === 'xlsx') return 'csv';
  return null;
}

export function ImportDialog({ organizationId, workspaceId, onSuccess, children }: ImportDialogProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);
  const [resultProjectId, setResultProjectId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const importMsProject = useImportMsProject();
  const importCsv = useImportCsv();

  const resetState = useCallback(() => {
    setStep('upload');
    setFile(null);
    setMappings([]);
    setResultProjectId(null);
    setErrorMessage('');
  }, []);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      resetState();
    }
  };

  const handleFileSelect = (selectedFile: File) => {
    const format = detectFormat(selectedFile.name);
    if (!format) {
      setErrorMessage(`Unsupported file type. Please select ${ACCEPTED_EXTENSIONS.join(', ')} files.`);
      setStep('error');
      return;
    }
    setFile(selectedFile);
    setErrorMessage('');
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleSubmit = async () => {
    if (!file) return;

    const format = detectFormat(file.name);
    if (!format) return;

    setStep('importing');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('organizationId', organizationId);
    formData.append('workspaceId', workspaceId);

    try {
      if (format === 'ms-project') {
        const result = await importMsProject.mutateAsync(formData);
        setResultProjectId(result.project.projectId);
        setStep('done');
        onSuccess?.(result.project.projectId);
      } else {
        const result = await importCsv.mutateAsync(formData);
        if (result.requiresMapping && result.mappings) {
          const hasLowConfidence = result.mappings.some((m) => m.confidence < 80);
          if (hasLowConfidence) {
            setMappings(result.mappings);
            setStep('mapping');
            return;
          }
        }
        if (result.project) {
          setResultProjectId(result.project.projectId);
          setStep('done');
          onSuccess?.(result.project.projectId);
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Import failed. Please try again.';
      setErrorMessage(message);
      setStep('error');
    }
  };

  const handleApplyMappings = async (updatedMappings: ColumnMapping[]) => {
    if (!file) return;

    setStep('importing');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('organizationId', organizationId);
    formData.append('workspaceId', workspaceId);
    formData.append('mappings', JSON.stringify(updatedMappings));

    try {
      const result = await importCsv.mutateAsync(formData);
      if (result.project) {
        setResultProjectId(result.project.projectId);
        setStep('done');
        onSuccess?.(result.project.projectId);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Import failed. Please try again.';
      setErrorMessage(message);
      setStep('error');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className={step === 'mapping' ? 'sm:max-w-2xl' : 'sm:max-w-lg'}>
        <DialogHeader>
          <DialogTitle>Import Project</DialogTitle>
          <DialogDescription>
            Import a project from MS Project XML, CSV, or Excel file.
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4">
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
              className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-muted-foreground/25 p-8 transition-colors hover:border-muted-foreground/50"
            >
              <Upload className="h-10 w-10 text-muted-foreground" />
              <div className="text-center">
                <p className="text-sm font-medium">Drop your file here or click to browse</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Supports .xml, .csv, .xlsx
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xml,.csv,.xlsx"
                onChange={handleInputChange}
                className="hidden"
              />
            </div>

            {file && (
              <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="truncate">{file.name}</span>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={!file}>
                Import
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'mapping' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Some columns could not be mapped automatically. Please review and adjust the mappings below.
            </p>
            <ColumnMapper
              mappings={mappings}
              onApply={handleApplyMappings}
              onCancel={() => setStep('upload')}
            />
          </div>
        )}

        {step === 'importing' && (
          <div className="flex flex-col items-center justify-center gap-3 py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Importing project...</p>
          </div>
        )}

        {step === 'done' && (
          <div className="flex flex-col items-center justify-center gap-3 py-8">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <p className="text-sm font-medium">Project imported successfully!</p>
            {resultProjectId && (
              <Button variant="link" asChild>
                <a href={`/projects/${resultProjectId}`}>View project</a>
              </Button>
            )}
            <DialogFooter className="mt-4 w-full">
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Close
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'error' && (
          <div className="flex flex-col items-center justify-center gap-3 py-8">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <p className="text-sm text-destructive">{errorMessage}</p>
            <DialogFooter className="mt-4 w-full">
              <Button variant="outline" onClick={resetState}>
                Try again
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
